import { gobalSearchKey } from "../Config/config";

// Helper to get accesscontrols from localStorage or use default
function getAccessControls() {
  const stored = typeof window !== 'undefined' ? localStorage.getItem('access_controls') : null;
  if (stored && stored !== "undefined") {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.log("Error parsing access controls from localStorage, using default values", e);
    }
  }
  return null; // Return null instead of undefined
}

export const accesscontrols = getAccessControls();

// Utility class to efficiently handle unique ID collection
class UniqueIdCollector {
  constructor() {
    this.sets = {};
    this.arrays = {};
  }

  initializeCollection(collectionName) {
    if (!this.sets[collectionName]) {
      this.sets[collectionName] = new Set();
      this.sets[collectionName + "_cmp_ids"] = new Set();
      this.arrays[collectionName] = [];
      this.arrays[collectionName + "_cmp_ids"] = [];
    }
  }

  addIds(collectionName, pk, companyId) {
    this.initializeCollection(collectionName);
    
    if (pk && !this.sets[collectionName].has(pk)) {
      this.sets[collectionName].add(pk);
      this.arrays[collectionName].push(pk);
    }
    
    if (companyId && !this.sets[collectionName + "_cmp_ids"].has(companyId)) {
      this.sets[collectionName + "_cmp_ids"].add(companyId);
      this.arrays[collectionName + "_cmp_ids"].push(companyId);
    }
  }

  getResults() {
    return this.arrays;
  }

  getStats(collectionName) {
    return {
      totalIds: this.sets[collectionName]?.size || 0,
      totalCompanyIds: this.sets[collectionName + "_cmp_ids"]?.size || 0
    };
  }
}

// Helper to build filter_by for pe_investment dynamically from accesscontrols
function getPeInvestmentFilterBy() {
  if (!accesscontrols) return ''; // Early return if no access controls
  
  const filters = [];
  // company__industry
  if (accesscontrols.peindustries && accesscontrols.peindustries.id && accesscontrols.peindustries.id.length > 0) {
    const inds = accesscontrols.peindustries.id.map(id => `company__industry:='${id}'`).join(' || ');
    filters.push(`(${inds})`);
  }
  // stage
  if (accesscontrols.pe_stage && accesscontrols.pe_stage.id && accesscontrols.pe_stage.id.length > 0) {
    const stages = accesscontrols.pe_stage.id.map(id => `stage:='${id}'`).join(' || ');
    filters.push(`(${stages})`);
  }
  // amount
  if (accesscontrols.pe_deal_range && (accesscontrols.pe_deal_range.from !== undefined && accesscontrols.pe_deal_range.to !== undefined)) {
    filters.push(`amount:[${accesscontrols.pe_deal_range.from}..${accesscontrols.pe_deal_range.to}]`);
  }
  // company__tags
  if (accesscontrols.pe_tags && accesscontrols.pe_tags.name && accesscontrols.pe_tags.name.length > 0) {
    const tags = accesscontrols.pe_tags.name.map(tag => `company__tags:=['${tag}']`).join(' || ');
    filters.push(`(${tags})`);
  }
  // company__country -> company__city__country__name
  if (accesscontrols.pe_countries && accesscontrols.pe_countries.id && accesscontrols.pe_countries.id.length > 0) {
    const countries = accesscontrols.pe_countries.id.map(id => `company__city__country__name:='${id}'`).join(' || ');
    filters.push(`(${countries})`);
  }
  // company__city -> company__city__name
  if (accesscontrols.pe_cities && accesscontrols.pe_cities.id && accesscontrols.pe_cities.id.length > 0) {
    const cities = accesscontrols.pe_cities.id.map(id => `company__city__name:='${id}'`).join(' || ');
    filters.push(`(${cities})`);
  }
  // company__region -> company__city__region
  if (accesscontrols.pe_regions && accesscontrols.pe_regions.id && accesscontrols.pe_regions.id.length > 0) {
    const regions = accesscontrols.pe_regions.id.map(id => `company__city__region:='${id}'`).join(' || ');
    filters.push(`(${regions})`);
  }
  // company__state -> company__city__state__name
  if (accesscontrols.pe_states && accesscontrols.pe_states.id && accesscontrols.pe_states.id.length > 0) {
    const states = accesscontrols.pe_states.id.map(id => `company__city__state__name:='${id}'`).join(' || ');
    filters.push(`(${states})`);
  }
  return filters.join(' && ');
}

// Helper to build filter_by for pe_exit, pe_ipo, angel, incubation dynamically from accesscontrols (no amount/stage)
function getPeLikeFilterBy() {
  if (!accesscontrols) return ''; // Early return if no access controls
  
  const filters = [];
  // company__industry
  if (accesscontrols.peindustries && accesscontrols.peindustries.id && accesscontrols.peindustries.id.length > 0) {
    const inds = accesscontrols.peindustries.id.map(id => `company__industry:='${id}'`).join(' || ');
    filters.push(`(${inds})`);
  }
  // company__tags
  if (accesscontrols.pe_tags && accesscontrols.pe_tags.name && accesscontrols.pe_tags.name.length > 0) {
    const tags = accesscontrols.pe_tags.name.map(tag => `company__tags:=['${tag}']`).join(' || ');
    filters.push(`(${tags})`);
  }
  return filters.join(' && ');
}

// Helper for pe_acquirer industry filter
function getPeAcquirerFilterBy() {
  if (!accesscontrols || !accesscontrols.peindustries || !accesscontrols.peindustries.id || accesscontrols.peindustries.id.length === 0) {
    return '';
  }
  const inds = accesscontrols.peindustries.id.map(id => `industry:='${id}'`).join(' || ');
  return `(${inds})`;
}

// Helper for cfs_company industry filter using cfsindustries as array of objects
function getCfsCompanyIndustryFilterBy() {
  if (!accesscontrols || !accesscontrols.cfsindustries || !Array.isArray(accesscontrols.cfsindustries) || accesscontrols.cfsindustries.length === 0) {
    return '';
  }
  const inds = accesscontrols.cfsindustries.map(obj => `industry:='${obj.id}'`).join(' || ');
  return `(${inds})`;
}

// Helper for merger_acquisition using ma* accesscontrols
function getMaMergerAcquisitionFilterBy() {
  if (!accesscontrols) return ''; // Early return if no access controls
  
  const filters = [];
  // company__industry
  if (accesscontrols.maindustries && accesscontrols.maindustries.id && accesscontrols.maindustries.id.length > 0) {
    const inds = accesscontrols.maindustries.id.map(id => `company__industry:='${id}'`).join(' || ');
    filters.push(`(${inds})`);
  }
  // company__country -> company__city__country__name
  if (accesscontrols.ma_countries && accesscontrols.ma_countries.id && accesscontrols.ma_countries.id.length > 0) {
    const countries = accesscontrols.ma_countries.id.map(id => `company__city__country__name:='${id}'`).join(' || ');
    filters.push(`(${countries})`);
  }
  // company__city -> company__city__name
  if (accesscontrols.ma_cities && accesscontrols.ma_cities.id && accesscontrols.ma_cities.id.length > 0) {
    const cities = accesscontrols.ma_cities.id.map(id => `company__city__name:='${id}'`).join(' || ');
    filters.push(`(${cities})`);
  }
  // company__region -> company__city__region
  if (accesscontrols.ma_regions && accesscontrols.ma_regions.id && accesscontrols.ma_regions.id.length > 0) {
    const regions = accesscontrols.ma_regions.id.map(id => `company__city__region:='${id}'`).join(' || ');
    filters.push(`(${regions})`);
  }
  // company__state -> company__city__state__name
  if (accesscontrols.ma_states && accesscontrols.ma_states.id && accesscontrols.ma_states.id.length > 0) {
    const states = accesscontrols.ma_states.id.map(id => `company__city__state__name:='${id}'`).join(' || ');
    filters.push(`(${states})`);
  }
  return filters.join(' && ');
}

export const TsSearchSchemaBase = [
  {
    collection: "pe_investment",
    query_by:
      "company__name,investors__name,more_info,company_id,advisors_for_company__name,advisors_for_investor__name,date,company__cin,company__tags,company__c_tag,company__sector,company__website,company__email,company__additional_info,company__board_info__name,company__management_info__name,company__cin__business_description,deal_round,stage",
    query_by_weights: "10,2,1,1,1,1,1,4,3,2,1,1,1,1,1,1,4,1,1",
    group_by: "company__name",
    group_limit: 99,
    sort_by: "_text_match:desc,date_int:desc",
    get filter_by() { 
      const filter = getPeInvestmentFilterBy();
      return filter || "";
    }
  },
  {
    collection: "pe_exit",
    query_by:
    "company__name,more_info,acquirer_name,exit_investors__name,investment_info,company_id,advisors_for_buyer__name,advisors_for_seller__name,date,company__cin,company__tags,company__c_tag,company__industry,company__sector,company__website,company__email,company__additional_info,company__board_info__name,company__management_info__name,company__cin__business_description,deal_type,is_vc,company__city__country__name,company__city__region,company__city__state__name,company__city__name",
    query_by_weights: "10,1,2,2,3,1,1,1,1,4,3,2,1,1,1,1,1,1,1,4,1,1,1,1,1,1",
    group_by: "company__name",
    group_limit:99,
    sort_by: "_text_match:desc,date_int:desc",
    get filter_by() { 
      const filter = getPeLikeFilterBy();
      return filter || "";
    }
  },
  
  {
    collection: "pe_ipo",
    query_by:
      "company__name,more_info,investment_details,selling_investors,ipo_investors__name,company_id,date,company__cin,company__tags,company__c_tag,company__industry,company__sector,company__website,company__email,company__additional_info,company__board_info__name,company__management_info__name,company__cin__business_description",
    query_by_weights: "10,2,1,3,3,1,1,4,3,2,1,1,1,1,1,1,1,4",
    get filter_by() { 
      const filter = getPeLikeFilterBy();
      return filter || "";
    }
  },

  {
    collection: "angel",
    query_by:
    "company__name,more_info,investors__name,company_id,date,company__cin,company__tags,company__c_tag,company__industry,company__sector,company__website,company__email,company__additional_info,company__board_info__name,company__management_info__name,company__cin__business_description,company__city__country__name,company__city__region,company__city__state__name,company__city__name",
    query_by_weights: "10,2,1,1,1,4,3,2,1,1,1,1,1,1,1,4,1,1,1,1",
    group_by: "company__name",
    group_limit:99,
    sort_by: "_text_match:desc,date_int:desc",
    get filter_by() { 
      const filter = getPeLikeFilterBy();
      return filter || "";
    }
  },

  // {
  //   collection: "incubation",
  //   query_by:
  //     "company__name,more_info,incubator__name,company_id,date,company__cin,company__tags,company__c_tag,company__industry,company__sector,company__website,company__email,company__additional_info,company__board_info__name,company__management_info__name,company__cin__business_description,company__city__country__name,company__city__region,company__city__state__name,company__city__name",
  //   query_by_weights: "10,2,1,1,1,4,3,2,1,1,1,1,1,1,1,4,1,1,1,1",
  //   get filter_by() { 
  //     const filter = getPeLikeFilterBy();
  //     return filter || "";
  //   }
  // },
  {
    collection: "pe_investor",
    query_by:
      "name,website,email,description,limited_partners,additional_info,key_contact",
    query_by_weights: "5,1,1,1,2,1,2",
  },
  {
    collection: "angel_investor",
    query_by: "name,website,email,description,limited_partners,additional_info,key_contact",
    query_by_weights: "5,1,1,1,2,1,2"
  },
  // { 
  //   collection: 'incubator',
  //   query_by: 'name,address,email,website,additional_info,management',
  //   query_by_weights: '5,1,1,2,3,2'
  // },
  {
    collection: "pe_fund",
    query_by: "fund__name,investor__name,manager,more_info,investor_id,date",
    query_by_weights: "3,2,1,1,2,1"
  },
  {
    collection: 'limited_partner',
    query_by: 'name,contact_person,email,website,institution_type',
    query_by_weights: '10,2,1,1,1'
  },
  {
    collection: "pe_advisor",
    query_by: "name,sector,email,website,contact_person,advisor_type",
    query_by_weights: "5,1,1,1,2,1",
  },
  {
    collection: "merger_acquisition",
    query_by:
      "company__name,acquirer__name,more_info,company_group,cin,old_cin,advisors_for_ma_company__name,advisors_for_ma_acquirer__name,company_id,date,company__cin,company__c_tag,company__industry,company__sector,company__website,company__email,company__additional_info,company__board_info__name,company__management_info__name,company__cin__business_description,company__city__country__name,company__city__region,company__city__state__name,company__city__name",
    query_by_weights: "10,3,2,1,1,1,2,2,1,1,4,2,1,1,1,1,1,1,1,4,1,1,1,1",
    group_by: "company__name",
    group_limit:99,
    sort_by: "_text_match:desc,date_int:desc",
    get filter_by() { 
      const filter = getMaMergerAcquisitionFilterBy();
      return filter || "";
    }
  },
  {
    collection: "pe_acquirer",
    query_by: "name,sector,industry,email,website,additional_info",
    query_by_weights: "5,1,2,1,1,2",
    get filter_by() { 
      const filter = getPeAcquirerFilterBy();
      return filter || "";
    }
  },
  {
    collection: "re_investment",
    query_by:
      "company__name,more_info,project_name,investors__name,company_id,date,company__sector,company__additional_info,company__board_info__name,company__management_info__name",
    query_by_weights: "10,1,2,2,1,1,1,1,1,1",
    // group_by: "company__name",
    // group_limit:99,
    // sort_by: "_text_match:desc,date_int:desc"
  },
  
  {
    collection: "re_exit",
    query_by:
      "company__name,more_info,acquirer__name,project_name,investment_details,company_id,investors__name,date,company__sector,company__additional_info,company__board_info__name,company__management_info__name",
    query_by_weights: "10,1,2,2,1,1,1,1,1,1,1,1",
    // group_by: "company__name",
    // group_limit:99,
    // sort_by: "_text_match:desc,date_int:desc"
  },

  {
    collection: "re_ipo",
    query_by:
      "company__name,more_info,investment_details,ipo_investors__name,date,company__sector,company__additional_info,company__board_info__name,company__management_info__name",
    query_by_weights: "10,2,1,1,1,1,1,1,1",
  },
  {
    collection: "re_investor",
    query_by:
      "name,website,email,description,limited_partners,additional_info,management_info__name",
    query_by_weights: "5,1,1,1,2,1,2",
  },
  {
    collection: "re_advisor",
    query_by: "name,sector,email,website,contact_person,advisor_type",
    query_by_weights: "5,1,1,1,2,1",
  },
  {
    collection: 're_fund',
    query_by: 'fund__name,investor__name,manager,more_info,investor_id,date',
    query_by_weights: '3,2,1,1,2,1'
  },
  {
    collection: 're_merger_acquisition',
    query_by: 'company__name,acquirer__name,more_info,company_id,date,company__cin,company__sector,company__website,company__email,company__additional_info,company__board_info__name,company__management_info__name',
    query_by_weights: '10,3,2,1,1,4,1,1,1,1,1,1'
  },
  {
    collection: "cfs_company",
    query_by:
      "full_name,short_name,cin,old_cin,previous_name,subsector,business_description,website,contact_name,auditor_name,tags,industry",
    query_by_weights: "10,5,9,8,5,3,2,1,1,1,5,1",
    get filter_by() { 
      const filter = getCfsCompanyIndustryFilterBy();
      return filter || "";
    }
  }
];

export const performSearch = async (searchParameters, retryCount = 3) => {
    for (let attempt = 1; attempt <= retryCount; attempt++) {
        try {
            const url = "https://v2-api-ts1.ventureintelligence.com/multi_search";
            const payload = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-Typesense-Api-Key': gobalSearchKey
                },
                body: JSON.stringify({ searches: searchParameters })
            };
            
            const resp = await fetch(url, payload);
            
            if (!resp.ok) {
                throw new Error(`HTTP error! status: ${resp.status}`);
            }
            
            const searchResults = await resp.json();
            
            // Validate response structure
            if (!searchResults || !Array.isArray(searchResults.results)) {
                throw new Error('Invalid response structure');
            }
            
            return searchResults;
        } catch (error) {
            console.warn(`Search attempt ${attempt}/${retryCount} failed:`, error);
            
            if (attempt === retryCount) {
                console.error('All search attempts failed:', error);
                return { results: [] };
            }
            
            // Exponential backoff: wait before retrying
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
    }
};


export const getSearchResultIdsAll = async (searchText="flipkart", results, searchType, collectionsToSearch, onProgress = null) => {
    const perPage = 250;
    const maxQueriesPerBatch = 50; // API limit for multi-search
    const maxRecordsPerBatch = 12500; // API limit for total records
    const idCollector = new UniqueIdCollector();
    
    console.log(collectionsToSearch, "collections for ids")
    
    if(!searchText){
      return null
    }

    // Filter only the required collections
    const filteredSchemas = TsSearchSchemaBase.filter(schema =>
        collectionsToSearch.includes(schema.collection)
    );

    let totalBatchesCount = 0;
    let completedBatchesCount = 0;

    // Calculate total batches for progress tracking
    filteredSchemas.forEach(schema => {
        const totalPages = Math.ceil(results / perPage);
        const batchesForCollection = Math.ceil(totalPages / maxQueriesPerBatch);
        totalBatchesCount += batchesForCollection;
    });

    // Process each collection separately to handle large datasets
    for (const schema of filteredSchemas) {
        const totalPages = Math.ceil(results / perPage);
        
        // Initialize collection in the UniqueIdCollector
        idCollector.initializeCollection(schema.collection);

        // Calculate how many batches we need
        const totalBatches = Math.ceil(totalPages / maxQueriesPerBatch);
        
        console.log(`Processing collection ${schema.collection}: ${totalPages} pages in ${totalBatches} batches`);

        // Process in batches to handle API limits
        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
            const startPage = batchIndex * maxQueriesPerBatch + 1;
            const endPage = Math.min(startPage + maxQueriesPerBatch - 1, totalPages);
            
            // Create queries for this batch
            const batchQueries = [];
            
            for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
                const baseQuery = {
                    collection: schema.collection,
                    query_by: schema.query_by,
                    include_fields: "pk, company_id",
                    enable_highlight_v1: "false",
                    highlight_fields: "none",
                    highlight_full_fields: "none",
                    per_page: perPage,
                    snippet_threshold: 20,
                    num_typos: searchType === 0 ? 2 : 0,
                    typo_tokens_threshold: searchType === 0 ? 10 : 0,
                    split_join_tokens: searchType === 0 ? "fallback" : "off",
                    drop_tokens_threshold: searchType === 0 ? 1 : 0,
                    q: searchType === 1 ? `"${searchText}"` : searchText,
                    page: pageNum,
                };

                if (schema.filter_by) {
                    baseQuery.filter_by = schema.filter_by;
                }

                batchQueries.push(baseQuery);
            }

            console.log(`Batch ${batchIndex + 1}/${totalBatches} for ${schema.collection}: pages ${startPage}-${endPage} (${batchQueries.length} queries)`);

            try {
                // Perform the search for this batch
                const respJson = await performSearch(batchQueries);

                // Process results from this batch using the UniqueIdCollector
                if (respJson && respJson.results) {
                    respJson.results.forEach((result) => {
                        const collectionName = result.request_params.collection_name;

                        result.hits.forEach((hit) => {
                            const pk = hit.document.pk;
                            const companyId = hit.document.company_id;
                            
                            // Use UniqueIdCollector for efficient duplicate removal
                            idCollector.addIds(collectionName, pk, companyId);
                        });
                    });
                }

                completedBatchesCount++;

                // Report progress if callback is provided
                if (onProgress && typeof onProgress === 'function') {
                    const progressPercent = Math.round((completedBatchesCount / totalBatchesCount) * 100);
                    const stats = idCollector.getStats(schema.collection);
                    
                    onProgress({
                        collection: schema.collection,
                        batchIndex: batchIndex + 1,
                        totalBatches,
                        completedBatches: completedBatchesCount,
                        totalBatchesOverall: totalBatchesCount,
                        progressPercent,
                        currentIds: stats.totalIds,
                        currentCompanyIds: stats.totalCompanyIds
                    });
                }

                // Add a small delay between batches to be respectful to the API
                if (batchIndex < totalBatches - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

            } catch (error) {
                console.error(`Error processing batch ${batchIndex + 1} for collection ${schema.collection}:`, error);
                completedBatchesCount++; // Still count as completed to maintain progress accuracy
                
                // Report error progress if callback is provided
                if (onProgress && typeof onProgress === 'function') {
                    onProgress({
                        collection: schema.collection,
                        batchIndex: batchIndex + 1,
                        totalBatches,
                        error: error.message,
                        completedBatches: completedBatchesCount,
                        totalBatchesOverall: totalBatchesCount,
                        progressPercent: Math.round((completedBatchesCount / totalBatchesCount) * 100)
                    });
                }
                
                // Continue with next batch even if one fails
                continue;
            }
        }

        const stats = idCollector.getStats(schema.collection);
        console.log(`Completed ${schema.collection}: collected ${stats.totalIds} unique IDs, ${stats.totalCompanyIds} unique company IDs`);
    }

    // Get final results from the UniqueIdCollector
    const KeyIds = idCollector.getResults();

    const finalSummary = Object.keys(KeyIds).reduce((summary, key) => {
        if (Array.isArray(KeyIds[key])) {
            summary[key] = KeyIds[key].length;
        }
        return summary;
    }, {});

    console.log("Final combined results:", finalSummary);

    // Report completion if callback is provided
    if (onProgress && typeof onProgress === 'function') {
        onProgress({
            completed: true,
            finalSummary,
            totalRecordsCollected: Object.keys(finalSummary).reduce((total, key) => {
                if (!key.includes('_cmp_ids')) {
                    total += finalSummary[key];
                }
                return total;
            }, 0)
        });
    }

    return KeyIds;
};

export const getCategoryWiseGlobalSearchIds = async (
  globalSearchResult,
  setGlobalSearchResult,
  targetCollection
) => {
  if (!targetCollection) {
    console.warn(`Unknown target collection: ${targetCollection}`);
    return [];
  }

  // 1. ✅ Check for existing results in globalSearchResult
  if (globalSearchResult?.[targetCollection]?.length > 0) {
    console.log(`Using existing results from globalSearchResult[${targetCollection}]`);
    return globalSearchResult;
  }

  let searchQuery = null;
  let searchType = null;
  let resultCount = 0;

  try {
    const stored = sessionStorage.getItem("SearchParams");
    if (stored) {
      const parsed = JSON.parse(stored);
      searchQuery = parsed.input;
      searchType = parsed.type;
      resultCount = parsed.found?.[targetCollection] || 0;
      console.log("Loaded SearchParams from sessionStorage:", parsed);
    }
  } catch (e) {
    console.error("Failed to parse SearchParams from sessionStorage:", e);
  }

  if (!searchQuery || searchType === undefined) {
    console.warn("Missing searchQuery or searchType — cannot perform standard search");
    return [];
  }

  try {

    const freshResults = await getSearchResultIdsAll(
      searchQuery,
      resultCount,
      searchType,
      [targetCollection]
    );

    if (freshResults && typeof freshResults === "object") {
      setGlobalSearchResult(prev => ({
        ...prev,
        ...freshResults,
      }));

      if (freshResults?.[targetCollection]?.length > 0) {
        console.log(`Fetched ${freshResults[targetCollection].length} results for ${targetCollection}`);
        return freshResults;
      }
    }
  } catch (err) {
    console.error("Error during standard global search:", err);
  }

  console.warn(`No results found for ${targetCollection}, returning empty array`);
  return [];
};